import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { 
  Truck, Plus, Loader2, Pencil, Trash2, ArrowLeft,
  Save, X, AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

// ==========================================
// CONFIGURAÇÃO DE PLACAS POR TIPO
// ==========================================
const TIPOS_VEICULO = [
  { value: 'Truck', label: 'Truck', placas: ['placa_principal'] },
  { value: 'Carreta', label: 'Carreta', placas: ['placa_principal', 'placa_2'] },
  { value: 'Bitrem', label: 'Bitrem', placas: ['placa_principal', 'placa_2', 'placa_3'] },
  { value: 'Rodotrem', label: 'Rodotrem', placas: ['placa_principal', 'placa_2', 'placa_3', 'placa_dolly'] }
];

const LABELS_PLACAS = {
  'placa_principal': 'Placa Principal (Cavalo)',
  'placa_2': 'Placa 2 (Carreta/1º Reboque)',
  'placa_3': 'Placa 3 (2º Reboque)',
  'placa_dolly': 'Placa do Dolly'
};

const formatarPlaca = (placa) => {
  if (!placa) return '';
  const limpo = placa.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (limpo.length === 7) return `${limpo.slice(0, 3)}-${limpo.slice(3)}`;
  return limpo;
};

export default function MeusVeiculos() {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  const [veiculos, setVeiculos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [motoristaId, setMotoristaId] = useState(null);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVeiculo, setEditingVeiculo] = useState(null);
  
  // Form com campos de placa dinâmicos
  const [formData, setFormData] = useState({
    tipo: '',
    placa_principal: '',
    placa_2: '',
    placa_3: '',
    placa_dolly: '',
    modelo: '',
    ano: '',
    cor: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ==========================================
  // CARREGAR DADOS DO SUPABASE
  // ==========================================
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.id) {
      carregarVeiculos();
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const carregarVeiculos = async () => {
    setIsLoading(true);
    try {
      // Buscar motorista_id pelo usuario_id
      const { data: motorista, error: errMotorista } = await supabase
        .from('motoristas')
        .select('id')
        .eq('usuario_id', user.id)
        .single();

      if (errMotorista) {
        console.error('Erro ao buscar motorista:', errMotorista);
        toast.error('Erro ao identificar motorista');
        setIsLoading(false);
        return;
      }

      if (motorista) {
        setMotoristaId(motorista.id);

        // Buscar caminhões do motorista
        const { data: caminhoes, error: errCaminhoes } = await supabase
          .from('caminhoes')
          .select('*')
          .eq('motorista_id', motorista.id)
          .order('created_at', { ascending: false });

        if (errCaminhoes) {
          console.error('Erro ao buscar caminhões:', errCaminhoes);
          toast.error('Erro ao carregar veículos');
        } else {
          setVeiculos(caminhoes || []);
        }
      }
    } catch (err) {
      console.error('Erro:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // HELPERS DO FORMULÁRIO
  // ==========================================
  const getPlacasDoTipo = (tipo) => {
    const config = TIPOS_VEICULO.find(t => t.value === tipo);
    return config ? config.placas : ['placa_principal'];
  };

  const resetForm = () => {
    setFormData({
      tipo: '',
      placa_principal: '',
      placa_2: '',
      placa_3: '',
      placa_dolly: '',
      modelo: '',
      ano: '',
      cor: ''
    });
    setEditingVeiculo(null);
    setDialogOpen(false);
  };

  const handleEdit = (veiculo) => {
    setEditingVeiculo(veiculo);
    setFormData({
      tipo: veiculo.tipo || '',
      placa_principal: veiculo.placa_principal || '',
      placa_2: veiculo.placa_2 || '',
      placa_3: veiculo.placa_3 || '',
      placa_dolly: veiculo.placa_dolly || '',
      modelo: veiculo.modelo || '',
      ano: veiculo.ano || '',
      cor: veiculo.cor || ''
    });
    setDialogOpen(true);
  };

  // ==========================================
  // CRUD NO SUPABASE
  // ==========================================
  const handleSubmit = async () => {
    if (!formData.tipo) {
      toast.error('Selecione o tipo de caminhão');
      return;
    }
    if (!formData.placa_principal) {
      toast.error('Informe a placa principal');
      return;
    }

    setIsSubmitting(true);

    try {
      const placasNecessarias = getPlacasDoTipo(formData.tipo);
      
      const dadosSalvar = {
        motorista_id: motoristaId,
        tipo: formData.tipo,
        placa_principal: formatarPlaca(formData.placa_principal),
        modelo: formData.modelo || null,
        ano: formData.ano || null,
        cor: formData.cor || null
      };

      // Adiciona placas extras conforme o tipo
      if (placasNecessarias.includes('placa_2')) {
        dadosSalvar.placa_2 = formatarPlaca(formData.placa_2) || null;
      }
      if (placasNecessarias.includes('placa_3')) {
        dadosSalvar.placa_3 = formatarPlaca(formData.placa_3) || null;
      }
      if (placasNecessarias.includes('placa_dolly')) {
        dadosSalvar.placa_dolly = formatarPlaca(formData.placa_dolly) || null;
      }

      if (editingVeiculo) {
        // UPDATE
        const { error } = await supabase
          .from('caminhoes')
          .update(dadosSalvar)
          .eq('id', editingVeiculo.id);

        if (error) throw error;
        toast.success('Veículo atualizado!');
      } else {
        // INSERT
        const { error } = await supabase
          .from('caminhoes')
          .insert([dadosSalvar]);

        if (error) throw error;
        toast.success('Veículo cadastrado!');
      }

      resetForm();
      carregarVeiculos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja remover este veículo?')) return;

    try {
      const { error } = await supabase
        .from('caminhoes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Veículo removido');
      carregarVeiculos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao remover: ' + error.message);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
        <span className="ml-2 text-slate-600">Verificando autenticação...</span>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const placasAtuais = getPlacasDoTipo(formData.tipo);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/perfil')}>
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl text-slate-800">Meus Veículos</span>
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#FF6B35] hover:bg-[#E85A2A]"
                  onClick={() => resetForm()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Veículo
                </Button>
              </DialogTrigger>
              
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingVeiculo ? 'Editar Veículo' : 'Cadastrar Novo Veículo'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Tipo de Caminhão */}
                  <div className="space-y-2">
                    <Label>Tipo de Caminhão *</Label>
                    <Select 
                      value={formData.tipo} 
                      onValueChange={(v) => setFormData({
                        ...formData, 
                        tipo: v,
                        placa_2: '',
                        placa_3: '',
                        placa_dolly: ''
                      })}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_VEICULO.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Placas conforme tipo */}
                  {formData.tipo && (
                    <div className="space-y-3">
                      {placasAtuais.map((placaKey) => (
                        <div key={placaKey} className="space-y-2">
                          <Label>
                            {LABELS_PLACAS[placaKey]}
                            {placaKey === 'placa_principal' ? ' *' : ''}
                          </Label>
                          <Input
                            placeholder="ABC-1234"
                            value={formData[placaKey]}
                            onChange={(e) => setFormData({
                              ...formData, 
                              [placaKey]: e.target.value.toUpperCase()
                            })}
                            className="uppercase"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Dados opcionais */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Modelo</Label>
                      <Input
                        placeholder="Volvo FH 540"
                        value={formData.modelo}
                        onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ano</Label>
                      <Input
                        placeholder="2024"
                        value={formData.ano}
                        onChange={(e) => setFormData({...formData, ano: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cor</Label>
                      <Input
                        placeholder="Vermelho"
                        value={formData.cor}
                        onChange={(e) => setFormData({...formData, cor: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Botões */}
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setDialogOpen(false)}
                    >
                      <X className="w-4 h-4 mr-2" /> Cancelar
                    </Button>
                    <Button 
                      className="flex-1 bg-[#FF6B35] hover:bg-[#E85A2A]"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> {editingVeiculo ? 'Salvar' : 'Cadastrar'}</>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
            <span className="ml-2 text-slate-600">Carregando veículos...</span>
          </div>
        ) : veiculos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {veiculos.map(veiculo => {
              const tipoConfig = TIPOS_VEICULO.find(t => t.value === veiculo.tipo);
              const placasVeiculo = tipoConfig ? tipoConfig.placas : ['placa_principal'];
              
              return (
                <Card key={veiculo.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                          <Truck className="w-6 h-6 text-[#FF6B35]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-lg">
                              {veiculo.placa_principal}
                            </CardTitle>
                            <Badge className="bg-slate-100 text-slate-700">
                              {veiculo.tipo}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {veiculo.modelo} {veiculo.ano}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEdit(veiculo)}
                        >
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(veiculo.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* Placas */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {placasVeiculo.map(placaKey => (
                        <div key={placaKey} className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">
                            {LABELS_PLACAS[placaKey]}
                          </p>
                          <p className="font-mono font-semibold text-slate-900">
                            {formatarPlaca(veiculo[placaKey]) || '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                    
                    {veiculo.cor && (
                      <p className="text-sm text-slate-500">
                        Cor: {veiculo.cor}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="text-center py-16 border-dashed border-2 border-slate-300">
            <CardContent>
              <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                Nenhum veículo cadastrado
              </h3>
              <p className="text-slate-600 mb-6">
                Cadastre seus veículos com as placas corretas
              </p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-[#FF6B35] hover:bg-[#E85A2A]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Primeiro Veículo
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info */}
        {veiculos.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
            <p className="text-sm text-blue-700">
              <strong>Dica:</strong> Mantenha as placas sempre atualizadas. 
              A BoraFrete usa essas informações para validação em portos e balanças.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}